import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await login(email, password);
      toast.success("Connexion réussie !");
      navigate("/dashboard");
    } catch (error) {
      const status = error.response?.status;
      const data = error.response?.data;
      let message;
      if (status === 429) {
        message = "Trop de tentatives de connexion. Veuillez réessayer dans quelques minutes.";
      } else {
        message = data?.detail || data?.error || "Erreur de connexion";
      }
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center pt-20 px-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-8 md:p-12 border border-border">
          <div className="text-center mb-8">
            <Link to="/" className="inline-block mb-6">
              <span className="font-heading text-3xl font-bold text-primary">
                ITERCATUS
              </span>
            </Link>
            <p className="text-xs text-muted-foreground italic mb-4">Le voyage intelligent</p>
            <h1 className="font-heading text-2xl font-bold text-primary">
              Connexion
            </h1>
            <p className="text-muted-foreground mt-2">
              Accédez à votre espace personnel
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Email
              </label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.com"
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-primary mb-2">
                Mot de passe
              </label>
              <Input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="h-12"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full btn-primary h-12"
              disabled={loading}
              data-testid="login-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Connexion...
                </>
              ) : (
                "Se connecter"
              )}
            </Button>

            <Button
              type="button"
              variant="outline"
              className="w-full h-12"
              disabled={loading || !email}
              data-testid="passkey-login-btn"
              onClick={async () => {
                if (!email) { toast.error("Renseignez votre email pour la connexion par passkey."); return; }
                setLoading(true);
                try {
                  const API = process.env.REACT_APP_BACKEND_URL + "/api";
                  const b64uToBuf = (s) => { const pad = "=".repeat((4-(s.length%4))%4); const b = (s+pad).replace(/-/g,"+").replace(/_/g,"/"); const raw = atob(b); const arr = new Uint8Array(raw.length); for (let i=0;i<raw.length;i++) arr[i]=raw.charCodeAt(i); return arr.buffer; };
                  const bufToB64u = (buf) => { const b = new Uint8Array(buf); let s=""; for (let i=0;i<b.length;i++) s+=String.fromCharCode(b[i]); return btoa(s).replace(/\+/g,"-").replace(/\//g,"_").replace(/=+$/g,""); };
                  const begin = await fetch(`${API}/auth/webauthn/begin`, { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({ email }) });
                  const bd = await begin.json();
                  if (!bd.ok) { toast.error("Authentification par passkey indisponible."); return; }
                  const opts = typeof bd.options === "string" ? JSON.parse(bd.options) : bd.options;
                  const publicKey = { ...opts, challenge: b64uToBuf(opts.challenge), allowCredentials: (opts.allowCredentials || []).map((c) => ({ ...c, id: b64uToBuf(c.id) })) };
                  const assertion = await navigator.credentials.get({ publicKey });
                  const payload = { id: assertion.id, rawId: bufToB64u(assertion.rawId), type: assertion.type, response: { clientDataJSON: bufToB64u(assertion.response.clientDataJSON), authenticatorData: bufToB64u(assertion.response.authenticatorData), signature: bufToB64u(assertion.response.signature), userHandle: assertion.response.userHandle ? bufToB64u(assertion.response.userHandle) : null } };
                  const finish = await fetch(`${API}/auth/webauthn/finish`, { method: "POST", headers: {"Content-Type":"application/json"}, body: JSON.stringify({ email, credential: payload }) });
                  if (!finish.ok) { toast.error("Passkey refusée."); return; }
                  const data = await finish.json();
                  localStorage.setItem("token", data.access_token);
                  toast.success("Connexion par passkey réussie !");
                  navigate("/dashboard");
                  window.location.reload();
                } catch (e) { toast.error("Passkey annulée ou échouée."); }
                finally { setLoading(false); }
              }}
            >
              Se connecter avec une passkey
            </Button>

            <div className="text-right">
              <Link
                to="/mot-de-passe-oublie"
                className="text-sm text-accent hover:underline"
                data-testid="forgot-password-link"
              >
                Mot de passe oublié ?
              </Link>
            </div>
          </form>

          <div className="mt-8 text-center">
            <p className="text-muted-foreground">
              Pas encore de compte ?{" "}
              <Link to="/inscription" className="text-accent hover:underline font-medium" data-testid="register-link">
                Créer un compte
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
